import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, MessageSquare, Phone, X } from 'lucide-react';
import MessageConversationPanel from './MessageConversationPanel';
import { useAuth } from '../context/AuthContext';
import type { ManagerInboxItem } from '../services/api';
import type { Employee, MessageConversation } from '../types';

interface InboxChatModalProps {
  item: ManagerInboxItem;
  messageId: number | null;
  conversations: MessageConversation[];
  onConversationsChange: (conversations: MessageConversation[]) => void;
  smsOpen?: boolean;
  sendingSms?: boolean;
  intent?: 'request_info' | 'ask_manager' | null;
  onClose: () => void;
  onMessageSent?: (text: string) => void;
  onSendSms: (message: string) => Promise<void>;
}

const InboxChatModal: React.FC<InboxChatModalProps> = ({
  item,
  messageId,
  conversations,
  onConversationsChange,
  smsOpen = false,
  sendingSms = false,
  intent = null,
  onClose,
  onMessageSent,
  onSendSms,
}) => {
  const { employee: currentEmployee } = useAuth();
  const [showSms, setShowSms] = useState(smsOpen);
  const [smsText, setSmsText] = useState('');
  const [smsError, setSmsError] = useState<string | null>(null);
  const hasPhone = Boolean(item.hasPhone);
  const askingManager = intent === 'ask_manager';
  const askingEmployee = intent === 'request_info';
  const managerName =
    item.viewerRole === 'escalated'
      ? item.ownerName
      : item.escalatedToName || item.ownerName;
  const askHeading = askingManager
    ? managerName || 'Manager'
    : askingEmployee
      ? item.employeeName
      : item.title || item.employeeName;
  const placeholder = askingManager
    ? `Message ${managerName || 'the manager'}…`
    : askingEmployee
      ? `Message ${item.employeeName}…`
      : 'Type a message…';

  const participantNames = useMemo(() => {
    const names: string[] = [];
    const add = (name?: string | null) => {
      const trimmed = (name || '').trim();
      if (!trimmed) return;
      if (names.some((n) => n.toLowerCase() === trimmed.toLowerCase())) return;
      names.push(trimmed);
    };
    add(item.employeeName);
    add(item.ownerName);
    add(item.escalatedToName);
    add(currentEmployee ? `${currentEmployee.firstName} ${currentEmployee.lastName}`.trim() : '');
    return names;
  }, [
    currentEmployee,
    item.employeeName,
    item.escalatedToName,
    item.ownerName,
  ]);

  useEffect(() => {
    if (smsOpen) setShowSms(true);
  }, [smsOpen]);

  const employees = useMemo<Employee[]>(() => {
    const stub = (id: number, name: string): Employee => {
      const parts = name.trim().split(/\s+/);
      return {
        id,
        firstName: parts[0] || name,
        lastName: parts.slice(1).join(' '),
        email: '',
        isActive: true,
        createdAt: '',
      };
    };
    const byId = new Map<number, Employee>();
    if (askingEmployee) {
      byId.set(item.employeeId, stub(item.employeeId, item.employeeName));
    } else if (askingManager) {
      const managerId =
        item.viewerRole === 'escalated'
          ? item.ownerEmployeeId
          : item.escalatedToEmployeeId || item.ownerEmployeeId;
      if (managerId && managerName) byId.set(managerId, stub(managerId, managerName));
    } else {
      byId.set(item.employeeId, stub(item.employeeId, item.employeeName));
      if (item.ownerEmployeeId && item.ownerName) {
        byId.set(item.ownerEmployeeId, stub(item.ownerEmployeeId, item.ownerName));
      }
      if (item.escalatedToEmployeeId && item.escalatedToName) {
        byId.set(item.escalatedToEmployeeId, stub(item.escalatedToEmployeeId, item.escalatedToName));
      }
    }
    if (currentEmployee) byId.set(currentEmployee.id, currentEmployee);
    const list: Employee[] = [];
    byId.forEach((emp) => {
      list.push(emp);
    });
    return list;
  }, [
    askingEmployee,
    askingManager,
    currentEmployee,
    item.employeeId,
    item.employeeName,
    item.escalatedToEmployeeId,
    item.escalatedToName,
    item.ownerEmployeeId,
    item.ownerName,
    item.viewerRole,
    managerName,
  ]);

  const submitSms = async () => {
    if (!hasPhone || !smsText.trim()) return;
    setSmsError(null);
    try {
      await onSendSms(smsText.trim());
      setSmsText('');
      setShowSms(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setSmsError(err?.response?.data?.message || err?.message || 'Could not send SMS.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full h-[80vh] flex flex-col">
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-neutral-800 truncate">{askHeading}</h2>
            <p className="text-sm text-neutral-500 truncate">{item.title}</p>
            <p className="text-xs text-neutral-400 truncate">
              {askingManager
                ? '1:1 with the manager'
                : askingEmployee
                  ? `1:1 with ${item.employeeName}`
                  : `Group · employee and managers in the line${
                      participantNames.length > 0 ? ` · ${participantNames.join(', ')}` : ''
                    }`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {askingEmployee && (
              <button
                type="button"
                disabled={!hasPhone || sendingSms}
                title={hasPhone ? 'Send SMS' : 'No phone number on employee profile'}
                onClick={() => {
                  setShowSms(true);
                  setSmsError(null);
                }}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingSms ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Phone className="h-3.5 w-3.5" />}
                Send SMS
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {showSms && (
          <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50 space-y-2">
            <label className="text-sm text-gray-700 block">
              SMS to {item.employeeName}
              <textarea
                className="mt-1 w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm bg-white"
                rows={2}
                value={smsText}
                onChange={(e) => setSmsText(e.target.value)}
                placeholder="Type the text message…"
              />
            </label>
            {smsError && <p className="text-sm text-red-700">{smsError}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="text-sm px-3 py-1.5 border border-gray-200 rounded-md bg-white"
                onClick={() => setShowSms(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!hasPhone || sendingSms || !smsText.trim()}
                className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md disabled:opacity-50"
                onClick={() => void submitSms()}
              >
                Send SMS
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col bg-neutral-50">
          {messageId ? (
            <MessageConversationPanel
              messageId={messageId}
              conversations={conversations}
              employees={employees}
              onConversationsChange={(next) => onConversationsChange(next)}
              currentEmployeeId={currentEmployee?.id}
              compact
              onMessageSent={onMessageSent}
              placeholder={placeholder}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-neutral-500 gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Opening chat…
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InboxChatModal;
